'use client';

/**
 * CONTACT & LOCATION SEARCH COMPONENT
 * 
 * Unified search component for selecting contacts through two methods:
 * 1. Find by Contact - Search contacts directly, then optionally select location
 * 2. Search Locations - Find location globally, auto-select its parent contact
 * 
 * Used in: Document modals (quotes, invoices, etc), Service jobs, Purchase orders
 * 
 * @version 1.0.0
 * @date December 2025
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Search,
  Building2,
  MapPin,
  ChevronDown,
  Check,
  Loader2,
  X,
  Phone,
  Mail,
  User,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { authService } from '@/lib/authService';
import {
  type ContactAccountStatus,
  calculateContactAccountStanding,
  formatContactAccountStatusLabel,
} from '@/lib/contacts/accountStanding';

// ============================================================================
// TYPES
// ============================================================================

export interface ContactSearchResult {
  id: number;
  contact_type: 'customer' | 'supplier' | 'partner' | 'other';
  display_name: string;
  company_name?: string;
  contact_person?: string;
  contact_number?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  billing_address?: string;
  is_active: boolean;
  location_count?: number;
  payment_terms_days?: number;
  credit_limit?: number;
  currency?: string;
  current_balance?: number;
  account_status?: ContactAccountStatus;
}

export interface LocationSearchResult {
  id: number;
  contact_id: number;
  location_name: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  is_primary?: boolean;
  is_active: boolean;
  // Joined contact info
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  contact_type?: string;
}

interface UnifiedSearchResult {
  type: 'contact' | 'location' | 'person';
  contact_id: number;
  contact_name: string;
  contact_company_name: string | null;
  contact_type: string;
  location_address?: string | null;
  person_name?: string | null;
  person_email?: string | null;
  person_phone?: string | null;
}

export interface ContactLocationSearchProps {
  // Selection state
  selectedContactId: number | null;
  selectedLocationId?: number | null;
  
  // Callbacks
  onContactSelect: (contact: ContactSearchResult | null) => void;
  onLocationSelect?: (location: LocationSearchResult | null) => void;
  
  // Configuration
  contactTypes?: Array<'customer' | 'vendor' | 'supplier' | 'partner' | 'other'>;
  showLocationSearch?: boolean;
  showLocationSelector?: boolean; // Show location dropdown after contact selection
  requireLocation?: boolean;
  label?: string;
  placeholder?: string;
  
  // Context
  documentType?: string;

  // Styling
  className?: string;
  isDark?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ContactLocationSearch({
  selectedContactId,
  selectedLocationId,
  onContactSelect,
  onLocationSelect,
  contactTypes = ['customer', 'vendor', 'supplier', 'partner', 'other'],
  showLocationSearch = true,
  showLocationSelector = false,
  requireLocation = false,
  label = 'Contact',
  placeholder = 'Search contacts...',
  documentType = 'job',
  className = '',
  isDark = false,
  size = 'md'
}: ContactLocationSearchProps) {
  
  // ============================================================================
  // STATE
  // ============================================================================
  
  const [mode, setMode] = useState<'contact' | 'location'>('contact');
  const [contacts, setContacts] = useState<ContactSearchResult[]>([]);
  const [locations, setLocations] = useState<LocationSearchResult[]>([]); // Contact's locations
  const [allLocations, setAllLocations] = useState<LocationSearchResult[]>([]); // Global search results
  
  const [contactSearch, setContactSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  
  const [isContactDropdownOpen, setIsContactDropdownOpen] = useState(false);
  const [isLocationSearchOpen, setIsLocationSearchOpen] = useState(false);
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingGlobalLocations, setLoadingGlobalLocations] = useState(false);
  
  const [selectedContact, setSelectedContact] = useState<ContactSearchResult | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<LocationSearchResult | null>(null);

  const [contactDropdownPos, setContactDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [locationSearchDropdownPos, setLocationSearchDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const contactRef = useRef<HTMLDivElement>(null);
  const locationSearchRef = useRef<HTMLDivElement>(null);
  const locationDropdownRef = useRef<HTMLDivElement>(null);
  
  // ============================================================================
  // UTILITIES
  // ============================================================================
  
  const getAuthToken = useCallback(() => {
    return authService.getTokens()?.access_token || '';
  }, []);

  // Size classes
  const sizeClasses = {
    sm: 'px-2 py-1.5 text-xs',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base'
  };
  
  const inputClasses = `w-full ${sizeClasses[size]} rounded-lg border transition-colors ${
    isDark
      ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400 focus:border-blue-500'
      : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400 focus:border-blue-500'
  } focus:ring-2 focus:ring-blue-500/20 focus:outline-none`;

  const dropdownClasses = `fixed z-[9999] mt-1 rounded-lg shadow-lg border max-h-64 overflow-hidden ${
    isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
  }`;

  const tabClasses = (active: boolean) => `flex-1 ${sizeClasses[size]} font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5 ${
    active
      ? 'bg-blue-500 text-white'
      : isDark
        ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
  }`;

  // Fixed-position dropdown helpers — escape overflow-hidden ancestors
  const measureDropdownPosition = useCallback((ref: React.RefObject<HTMLDivElement | null>) => {
    const el = ref.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return {
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    };
  }, []);

  const updateContactDropdownPos = useCallback(() => {
    const pos = measureDropdownPosition(contactRef);
    if (pos) setContactDropdownPos(pos);
  }, [measureDropdownPosition]);

  const updateLocationSearchDropdownPos = useCallback(() => {
    const pos = measureDropdownPosition(locationSearchRef);
    if (pos) setLocationSearchDropdownPos(pos);
  }, [measureDropdownPosition]);

  // ============================================================================
  // EFFECTS
  // ============================================================================
  
  // Track if initial load is done
  const initialLoadDoneRef = useRef(false);
  const lastLoadedContactIdRef = useRef<number | null>(null);
  const lastLoadedLocationIdRef = useRef<number | null>(null);
  const lastContactTypesRef = useRef<string>(contactTypes.join(','));
  
  // Update selected contact when ID changes externally (use ref to prevent loops)
  const contactsRef = useRef<ContactSearchResult[]>([]);
  useEffect(() => {
    contactsRef.current = contacts;
  }, [contacts]);
  
  useEffect(() => {
    if (selectedContactId && !selectedContact) {
      const contact = contactsRef.current.find(c => c.id === selectedContactId);
      if (contact) {
        setSelectedContact(contact);
      }
    } else if (!selectedContactId && selectedContact) {
      setSelectedContact(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContactId]); // Only depend on selectedContactId

  // Update selected location when ID changes externally (use ref to prevent loops)
  const locationsRef = useRef<LocationSearchResult[]>([]);
  useEffect(() => {
    locationsRef.current = locations;
  }, [locations]);
  
  useEffect(() => {
    if (selectedLocationId && selectedLocationId !== lastLoadedLocationIdRef.current) {
      lastLoadedLocationIdRef.current = selectedLocationId;
      const location = locationsRef.current.find(l => l.id === selectedLocationId);
      if (location) {
        setSelectedLocation(location);
      }
    } else if (!selectedLocationId && lastLoadedLocationIdRef.current !== null) {
      lastLoadedLocationIdRef.current = null;
      setSelectedLocation(null);
    }
  }, [selectedLocationId]); // Only depend on selectedLocationId

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (contactRef.current && !contactRef.current.contains(e.target as Node)) {
        setIsContactDropdownOpen(false);
      }
      if (locationSearchRef.current && !locationSearchRef.current.contains(e.target as Node)) {
        setIsLocationSearchOpen(false);
      }
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(e.target as Node)) {
        setIsLocationDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Position fixed dropdowns when they open
  useEffect(() => {
    if (isContactDropdownOpen) {
      updateContactDropdownPos();
    }
  }, [isContactDropdownOpen, updateContactDropdownPos]);

  useEffect(() => {
    if (isLocationSearchOpen) {
      updateLocationSearchDropdownPos();
    }
  }, [isLocationSearchOpen, updateLocationSearchDropdownPos]);

  // Re-position on resize/scroll while any dropdown is open
  useEffect(() => {
    if (!isContactDropdownOpen && !isLocationSearchOpen) return;
    const handle = () => {
      if (isContactDropdownOpen) updateContactDropdownPos();
      if (isLocationSearchOpen) updateLocationSearchDropdownPos();
    };
    window.addEventListener('resize', handle);
    window.addEventListener('scroll', handle, true);
    return () => {
      window.removeEventListener('resize', handle);
      window.removeEventListener('scroll', handle, true);
    };
  }, [isContactDropdownOpen, isLocationSearchOpen, updateContactDropdownPos, updateLocationSearchDropdownPos]);

  // ============================================================================
  // API CALLS
  // ============================================================================
  
  const loadContacts = async (search: string) => {
    try {
      setLoadingContacts(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('types', 'contact,location,person'); // Search all three types
      params.append('limit', '50');
      // Always pass contact_types to filter - critical for document modals
      if (contactTypes.length > 0) {
        params.append('contact_types', contactTypes.join(','));
      }
      
      const response = await fetch(`/api/v1/contacts/search/unified?${params}`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      
      if (response.ok) {
        const { data } = await response.json();
        // Transform unified results to ContactSearchResult format
        // For location and person results, we map them to their parent contact
        const transformed = (data || []).map((result: any) => {
          if (result.type === 'contact') {
            return {
              id: result.contact_id,
              contact_type: result.contact_type,
              display_name: result.contact_name || 'Unnamed',
              company_name: result.contact_company_name,
              contact_person: result.contact_name,
              email: null,
              phone: null,
              mobile: null,
              billing_address: null,
              is_active: true,
              location_count: 0,
              payment_terms_days: null,
              credit_limit: null,
              currency: null,
              current_balance: null,
              account_status: null,
            };
          } else if (result.type === 'location') {
            // Location result - map to parent contact
            return {
              id: result.contact_id,
              contact_type: result.contact_type,
              display_name: result.contact_name || 'Unnamed',
              company_name: result.contact_company_name,
              contact_person: result.contact_name,
              email: null,
              phone: null,
              mobile: null,
              billing_address: result.location_address,
              is_active: true,
              location_count: 0,
              payment_terms_days: null,
              credit_limit: null,
              currency: null,
              current_balance: null,
              account_status: null,
            };
          } else if (result.type === 'person') {
            // Person result - map to parent contact
            return {
              id: result.contact_id,
              contact_type: result.contact_type,
              display_name: result.contact_name || 'Unnamed',
              company_name: result.contact_company_name,
              contact_person: result.person_name,
              email: result.person_email,
              phone: result.person_phone,
              mobile: result.person_phone,
              billing_address: null,
              is_active: true,
              location_count: 0,
              payment_terms_days: null,
              credit_limit: null,
              currency: null,
              current_balance: null,
              account_status: null,
            };
          }
          return null;
        }).filter((c: ContactSearchResult | null) => c !== null);
        
        // Deduplicate by contact_id
        const uniqueContacts = new Map<number, ContactSearchResult>();
        transformed.forEach((c: ContactSearchResult) => {
          if (!uniqueContacts.has(c.id)) {
            uniqueContacts.set(c.id, c);
          }
        });
        setContacts(Array.from(uniqueContacts.values()));
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoadingContacts(false);
    }
  };

  const loadContactLocations = async (contactId: number) => {
    try {
      setLoadingLocations(true);
      const response = await fetch(`/api/v1/contacts/${contactId}/locations`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      
      if (response.ok) {
        const { data } = await response.json();
        setLocations(data || []);
      }
    } catch (error) {
      console.error('Error loading contact locations:', error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const searchAllLocations = async (search: string) => {
    try {
      setLoadingGlobalLocations(true);
      const response = await fetch(`/api/v1/operations/locations?search=${encodeURIComponent(search)}&with_contacts=true`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      
      if (response.ok) {
        const { data } = await response.json();
        // Filter locations by allowed contact types
        const filteredLocations = (data || []).filter((loc: LocationSearchResult) => {
          // If no contact type filtering is needed (all types allowed), include all
          if (contactTypes.length >= 5) return true;
          // Normalize contact types: 'vendor' and 'supplier' are treated the same
          const locType = loc.contact_type || '';
          const normalizedLocType = locType === 'supplier' ? 'vendor' : locType;
          const normalizedAllowedTypes = contactTypes.map(t => t === 'supplier' ? 'vendor' : t) as Array<'customer' | 'vendor' | 'partner' | 'other'>;
          return normalizedAllowedTypes.includes(normalizedLocType as 'customer' | 'vendor' | 'partner' | 'other');
        });
        setAllLocations(filteredLocations);
      }
    } catch (error) {
      console.error('Error searching locations:', error);
    } finally {
      setLoadingGlobalLocations(false);
    }
  };

  // Load initial contacts - reload when contactTypes changes
  useEffect(() => {
    const typesKey = contactTypes.join(',');
    if (!initialLoadDoneRef.current || typesKey !== lastContactTypesRef.current) {
      initialLoadDoneRef.current = true;
      lastContactTypesRef.current = typesKey;
      loadContacts('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactTypes.join(',')]);  // Reload when contact types change

  // Load contact's locations when selected (only if ID actually changed)
  useEffect(() => {
    if (selectedContactId && selectedContactId !== lastLoadedContactIdRef.current) {
      lastLoadedContactIdRef.current = selectedContactId;
      loadContactLocations(selectedContactId);
    } else if (!selectedContactId && lastLoadedContactIdRef.current !== null) {
      lastLoadedContactIdRef.current = null;
      setLocations([]);
      setSelectedLocation(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContactId]);

  // Debounced location search
  useEffect(() => {
    if (mode === 'location' && locationSearch.length >= 2) {
      const timer = setTimeout(() => {
        searchAllLocations(locationSearch);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setAllLocations([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationSearch, mode]);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  
  const handleContactSelect = (contact: ContactSearchResult) => {
    setSelectedContact(contact);
    setSelectedLocation(null);
    onContactSelect(contact);
    onLocationSelect?.(null);
    setIsContactDropdownOpen(false);
    setContactSearch('');
  };

  const handleLocationSelect = (location: LocationSearchResult | null) => {
    setSelectedLocation(location);
    onLocationSelect?.(location);
    setIsLocationDropdownOpen(false);
  };

  const handleGlobalLocationSelect = async (location: LocationSearchResult) => {
    // Auto-select the contact
    if (location.contact_id) {
      // Find or fetch the contact
      let contact = contacts.find(c => c.id === location.contact_id);
      
      if (!contact) {
        // Fetch the contact
        try {
          const response = await fetch(`/api/v1/contacts/${location.contact_id}`, {
            headers: { 'Authorization': `Bearer ${getAuthToken()}` }
          });
          if (response.ok) {
            const { data } = await response.json();
            contact = {
              id: data.id,
              contact_type: data.contact_type,
              display_name: data.display_name || data.company_name || data.contact_person || 'Unnamed',
              company_name: data.company_name,
              contact_person: data.contact_person,
              email: data.email,
              phone: data.phone || data.mobile,
              mobile: data.mobile,
              billing_address: data.billing_address,
              is_active: data.is_active,
              payment_terms_days: data.payment_terms_days,
              credit_limit: data.credit_limit,
              currency: data.currency,
              current_balance: data.current_balance,
              account_status: data.account_status,
            };
          }
        } catch (error) {
          console.error('Error fetching contact:', error);
        }
      }
      
      if (contact) {
        setSelectedContact(contact);
        onContactSelect(contact);
        
        // Also set the location
        setSelectedLocation(location);
        onLocationSelect?.(location);
      }
    }
    
    setIsLocationSearchOpen(false);
    setLocationSearch('');
    setMode('contact'); // Switch back to show selection
  };

  const handleClear = () => {
    setSelectedContact(null);
    setSelectedLocation(null);
    onContactSelect(null);
    onLocationSelect?.(null);
  };

  // Filter contacts by search
  const filteredContacts = contacts.filter(c => {
    if (!contactSearch) return true;
    const searchLower = contactSearch.toLowerCase();
    return (
      c.display_name?.toLowerCase().includes(searchLower) ||
      c.company_name?.toLowerCase().includes(searchLower) ||
      c.contact_person?.toLowerCase().includes(searchLower) ||
      c.email?.toLowerCase().includes(searchLower)
    );
  });

  // ============================================================================
  // RENDER
  // ============================================================================
  
  return (
    <div className={`space-y-3 ${className}`}>
      {/* Label */}
      {label && (
        <label className={`block text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {label} {requireLocation && '*'}
        </label>
      )}

      {/* Mode Toggle */}
      {showLocationSearch && (
        <div className={`flex gap-1 p-1 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
          <button
            type="button"
            onClick={() => setMode('contact')}
            className={tabClasses(mode === 'contact')}
          >
            <Building2 size={size === 'sm' ? 14 : 16} />
            Find by Contact
          </button>
          <button
            type="button"
            onClick={() => setMode('location')}
            className={tabClasses(mode === 'location')}
          >
            <MapPin size={size === 'sm' ? 14 : 16} />
            Search Locations
          </button>
        </div>
      )}

      {mode === 'contact' ? (
        <>
          {/* Contact Search Dropdown */}
          <div className="relative" ref={contactRef}>
            <div
              className={`w-full flex items-center justify-between gap-2 ${sizeClasses[size]} rounded-lg border transition-colors cursor-pointer ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-gray-200 hover:border-gray-500'
                  : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
              }`}
            >
              <div 
                className="flex items-center gap-2 min-w-0 flex-1"
                onClick={() => setIsContactDropdownOpen(!isContactDropdownOpen)}
              >
                <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                {selectedContact ? (
                  <div className="flex-1 min-w-0 text-left">
                    <span className="truncate block">{selectedContact.display_name}</span>
                  </div>
                ) : (
                  <span className="text-gray-400">{placeholder}</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {selectedContact && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClear();
                    }}
                    className={`p-1 rounded hover:bg-gray-200 ${isDark ? 'hover:bg-gray-600' : ''}`}
                  >
                    <X size={14} />
                  </button>
                )}
                <div 
                  onClick={() => setIsContactDropdownOpen(!isContactDropdownOpen)}
                  className="p-1"
                >
                  <ChevronDown className={`w-4 h-4 transition-transform ${isContactDropdownOpen ? 'rotate-180' : ''}`} />
                </div>
              </div>
            </div>

            {isContactDropdownOpen && (
              <div className={dropdownClasses} style={contactDropdownPos ? { top: contactDropdownPos.top, left: contactDropdownPos.left, width: contactDropdownPos.width } : undefined}>
                {/* Search Input */}
                <div className={`p-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={contactSearch}
                      onChange={(e) => {
                        setContactSearch(e.target.value);
                        if (e.target.value.length >= 2) {
                          loadContacts(e.target.value);
                        }
                      }}
                      placeholder="Search by name, email..."
                      className={`w-full pl-8 pr-3 py-1.5 text-sm rounded border ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-gray-200'
                          : 'bg-gray-50 border-gray-200 text-gray-700'
                      } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                      autoFocus
                    />
                  </div>
                </div>

                {/* Contact List */}
                <div className="max-h-52 overflow-y-auto">
                  {loadingContacts ? (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto mb-1" />
                      Loading contacts...
                    </div>
                  ) : filteredContacts.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      No contacts found
                    </div>
                  ) : (
                    filteredContacts.map(contact => (
                      <button
                        key={contact.id}
                        type="button"
                        onClick={() => handleContactSelect(contact)}
                        className={`w-full flex items-start gap-3 p-3 text-left transition-colors border-b last:border-b-0 ${
                          isDark ? 'hover:bg-gray-700 border-gray-700' : 'hover:bg-gray-50 border-gray-100'
                        } ${selectedContactId === contact.id ? (isDark ? 'bg-gray-700' : 'bg-blue-50') : ''}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isDark ? 'bg-gray-600' : 'bg-gray-100'
                        }`}>
                          <Building2 className="w-4 h-4 text-gray-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                            {contact.display_name}
                          </div>
                          {contact.email && (
                            <div className="text-xs text-gray-500 truncate flex items-center gap-1">
                              <Mail size={10} />
                              {contact.email}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-1.5 py-0.5 rounded capitalize ${
                              contact.contact_type === 'customer' ? 'bg-blue-100 text-blue-700' :
                              contact.contact_type === 'supplier' ? 'bg-purple-100 text-purple-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {contact.contact_type}
                            </span>
                            {contact.location_count && contact.location_count > 0 && (
                              <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                <MapPin size={10} />
                                {contact.location_count} location{contact.location_count > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                        {selectedContactId === contact.id && (
                          <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Selected Contact Details */}
          {selectedContact && (
            <ContactAccountStandingBlock
              contact={selectedContact}
              documentType={documentType}
              isDark={isDark}
            />
          )}

          {/* Location Selector (when contact selected) */}
          {showLocationSelector && selectedContactId && (
            <div className="relative" ref={locationDropdownRef}>
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Location {requireLocation && '*'}
              </label>
              
              {loadingLocations ? (
                <div className={`flex items-center gap-2 ${sizeClasses[size]} rounded-lg border ${
                  isDark ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'
                }`}>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading locations...
                </div>
              ) : locations.length === 0 ? (
                <div className={`${sizeClasses[size]} rounded-lg border ${
                  isDark ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'
                }`}>
                  No locations for this contact
                </div>
              ) : (
                <select
                  value={selectedLocationId || ''}
                  onChange={(e) => {
                    const locId = e.target.value ? parseInt(e.target.value) : null;
                    const location = locations.find(l => l.id === locId) || null;
                    handleLocationSelect(location!);
                  }}
                  className={inputClasses}
                  required={requireLocation}
                >
                  <option value="">Select location...</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.location_name}
                      {loc.city ? ` - ${loc.city}` : ''}
                      {loc.is_primary ? ' (Primary)' : ''}
                    </option>
                  ))}
                </select>
              )}

              {/* Selected Location Details */}
              {selectedLocation && (
                <div className={`mt-2 p-3 rounded-lg text-sm ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-0.5 text-blue-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                        {selectedLocation.location_name}
                        {selectedLocation.is_primary && (
                          <span className="ml-2 text-xs text-blue-500">(Primary)</span>
                        )}
                      </p>
                      {selectedLocation.address_line1 && (
                        <p className="text-xs text-gray-500">{selectedLocation.address_line1}</p>
                      )}
                      {(selectedLocation.city || selectedLocation.postal_code) && (
                        <p className="text-xs text-gray-500">
                          {[selectedLocation.city, selectedLocation.state, selectedLocation.postal_code]
                            .filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        /* Location Search Mode */
        <div className="relative" ref={locationSearchRef}>
          <p className={`text-xs mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Search by location name, address, or city. The contact will be auto-selected.
          </p>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={locationSearch}
              onChange={(e) => {
                setLocationSearch(e.target.value);
                setIsLocationSearchOpen(e.target.value.length >= 2);
              }}
              onFocus={() => locationSearch.length >= 2 && setIsLocationSearchOpen(true)}
              placeholder="Type at least 2 characters to search..."
              className={`${inputClasses} pl-9`}
            />
            {loadingGlobalLocations && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
            )}
          </div>

          {isLocationSearchOpen && locationSearch.length >= 2 && (
            <div className={dropdownClasses} style={locationSearchDropdownPos ? { top: locationSearchDropdownPos.top, left: locationSearchDropdownPos.left, width: locationSearchDropdownPos.width } : undefined}>
              {loadingGlobalLocations ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto mb-1" />
                  Searching locations...
                </div>
              ) : allLocations.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No locations found
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto">
                  {allLocations.map(location => (
                    <button
                      key={location.id}
                      type="button"
                      onClick={() => handleGlobalLocationSelect(location)}
                      className={`w-full flex items-start gap-3 p-3 text-left transition-colors border-b last:border-b-0 ${
                        isDark ? 'hover:bg-gray-700 border-gray-700' : 'hover:bg-gray-50 border-gray-100'
                      }`}
                    >
                      <MapPin className="w-4 h-4 mt-0.5 text-blue-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                          {location.location_name}
                          {location.is_primary && (
                            <span className="ml-1.5 text-xs text-blue-500">(Primary)</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {[location.address_line1, location.city, location.postal_code]
                            .filter(Boolean).join(', ')}
                        </div>
                        {location.contact_name && (
                          <div className={`text-xs mt-1 flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            <Building2 size={12} />
                            {location.contact_name}
                            {location.contact_type && (
                              <span className={`ml-1 px-1 py-0.5 rounded text-[10px] capitalize ${
                                location.contact_type === 'customer' ? 'bg-blue-100 text-blue-700' :
                                location.contact_type === 'supplier' ? 'bg-purple-100 text-purple-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {location.contact_type}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// ACCOUNT STANDING BLOCK
// Compact inline display of contact account standing.
// ============================================================================

function ContactAccountStandingBlock({
  contact,
  documentType,
  isDark,
}: {
  contact: ContactSearchResult;
  documentType: string;
  isDark: boolean;
}) {
  const standingSnapshot = useMemo(
    () => calculateContactAccountStanding(contact, 0, documentType),
    [contact, documentType]
  );

  if (!standingSnapshot) return null;

  const accountStanding = standingSnapshot.currentStanding;
  const creditLimit = standingSnapshot.creditLimit;
  const currentBalance = standingSnapshot.currentBalance;
  const availableCredit = standingSnapshot.availableCredit;
  const creditUtilization = standingSnapshot.currentUtilization || 0;
  const accountStatusLabel = formatContactAccountStatusLabel(contact.account_status, contact.is_active);

  const formatCurrency = (amount: number): string => {
    return (contact.currency || 'ZAR') + ' ' + new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getStatusColor = () => {
    switch (accountStanding.status) {
      case 'good':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'blocked':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = () => {
    switch (accountStanding.status) {
      case 'good':
        return <CheckCircle className="w-3.5 h-3.5" />;
      case 'warning':
        return <AlertTriangle className="w-3.5 h-3.5" />;
      case 'blocked':
        return <XCircle className="w-3.5 h-3.5" />;
      default:
        return <CheckCircle className="w-3.5 h-3.5" />;
    }
  };

  return (
    <div className={`rounded-lg p-3 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
            {contact.display_name}
          </p>
          {contact.contact_person && contact.company_name && (
            <p className="text-xs text-gray-500">{contact.contact_person}</p>
          )}
          <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
            {contact.email && (
              <span className="flex items-center gap-1">
                <Mail size={12} /> {contact.email}
              </span>
            )}
            {contact.phone && (
              <span className="flex items-center gap-1">
                <Phone size={12} /> {contact.phone}
              </span>
            )}
          </div>
        </div>
        <span className={`text-xs px-2 py-1 rounded capitalize flex-shrink-0 ${
          contact.contact_type === 'customer' ? 'bg-blue-100 text-blue-700' :
          contact.contact_type === 'supplier' ? 'bg-purple-100 text-purple-700' :
          'bg-gray-100 text-gray-600'
        }`}>
          {contact.contact_type}
        </span>
      </div>

      {/* Account Standing */}
      <div className={`mt-2 pt-2 border-t ${isDark ? 'border-gray-600/50' : 'border-gray-200/50'}`}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${getStatusColor()}`}>
            {getStatusIcon()}
            <span>{accountStanding.message}</span>
          </div>
          {creditLimit > 0 && (
            <span className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {formatCurrency(currentBalance)} / {formatCurrency(creditLimit)}
            </span>
          )}
        </div>

        {accountStanding.status !== 'good' && (
          <div className={`mt-1.5 p-1.5 rounded-lg border text-[11px] flex items-start gap-1.5 ${
            accountStanding.status === 'blocked'
              ? 'bg-red-50 border-red-300 text-red-800'
              : 'bg-yellow-50 border-yellow-300 text-yellow-800'
          }`}>
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold">{accountStanding.message}: </span>
              {accountStanding.reasons[0]}
              {accountStanding.reasons.length > 1 && (
                <span className="opacity-75"> +{accountStanding.reasons.length - 1} more</span>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 mt-2">
          <div>
            <div className={`text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Status</div>
            <div className={`text-[11px] font-semibold ${
              accountStanding.status === 'blocked' ? 'text-red-600'
                : accountStanding.status === 'warning' ? 'text-yellow-600'
                : isDark ? 'text-gray-100' : 'text-gray-900'
            }`}>{accountStatusLabel}</div>
          </div>
          <div>
            <div className={`text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Outstanding</div>
            <div className={`text-[11px] font-semibold ${currentBalance > 0 ? 'text-orange-600' : isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              {formatCurrency(currentBalance)}
            </div>
          </div>
          {creditLimit > 0 && (
            <div>
              <div className={`text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Available</div>
              <div className={`text-[11px] font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                {formatCurrency(availableCredit)}
              </div>
            </div>
          )}
        </div>

        {creditLimit > 0 && (
          <div className="mt-1.5">
            <div className={`w-full h-1 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <div
                className={`h-full transition-all ${
                  creditUtilization >= 100 ? 'bg-red-500' : creditUtilization >= 80 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(creditUtilization, 100)}%` }}
              />
            </div>
            <div className={`text-[10px] mt-0.5 text-right ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              {creditUtilization.toFixed(0)}% utilized
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// SIMPLE CONTACT SELECT (without location search)
// ============================================================================

export function SimpleContactSelect({
  selectedContactId,
  onContactSelect,
  contactTypes = ['customer', 'vendor', 'supplier', 'partner', 'other'],
  label = 'Contact',
  placeholder = 'Select contact...',
  className = '',
  isDark = false,
  size = 'md'
}: Omit<ContactLocationSearchProps, 'showLocationSearch' | 'showLocationSelector' | 'requireLocation' | 'selectedLocationId' | 'onLocationSelect'>) {
  return (
    <ContactLocationSearch
      selectedContactId={selectedContactId}
      onContactSelect={onContactSelect}
      contactTypes={contactTypes}
      showLocationSearch={false}
      showLocationSelector={false}
      label={label}
      placeholder={placeholder}
      className={className}
      isDark={isDark}
      size={size}
    />
  );
}
